import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import { CATEGORY_ICONS } from "@/lib/constants";
import { Star, BadgeCheck, MapPin, CalendarDays, Award, Send } from "lucide-react";

function StarRow({ value = 0, size = "h-4 w-4" }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
      ))}
    </div>
  );
}

function fmtMonthYear(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function MerchantPublicProfile() {
  const { id } = useParams();
  const { t, tCat, tCity, lang } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/merchants/${id}/public`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);

  if (err) return (
    <div className="app-shell p-5" data-testid="merchant-profile-page">
      <TopBar title={t("merchant")} />
      <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg p-3">{err}</div>
    </div>
  );
  if (!data) return (
    <div className="app-shell p-5" data-testid="merchant-profile-page">
      <TopBar title={t("merchant")} />
      <div className="grid place-items-center mt-20">
        <div className="h-10 w-10 rounded-full border-4 border-emerald-200 border-t-emerald-600 animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="app-shell pb-12" data-testid="merchant-profile-page">
      <TopBar title={t("merchantProfile")} />
      <div className="px-5 pt-4">
        {/* Header card */}
        <div className="card-surface p-5">
          <div className="flex items-start gap-4">
            {data.logo ? (
              <img src={data.logo} alt="" className="h-16 w-16 rounded-2xl object-cover" />
            ) : (
              <div className="h-16 w-16 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center font-bold text-xl font-display">
                {(data.business_name || "?")[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-xl font-bold truncate" data-testid="profile-name">{data.business_name}</h1>
                {data.verified && (
                  <span data-testid="verified-badge" className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                    <BadgeCheck className="h-3.5 w-3.5" /> {t("verified")}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {tCity(data.city)}
              </div>
              {data.rating != null ? (
                <div className="mt-2 flex items-center gap-2" data-testid="profile-rating">
                  <StarRow value={data.rating} />
                  <span className="text-sm font-semibold text-zinc-900">{data.rating.toFixed(1)}</span>
                  <span className="text-xs text-zinc-500">({data.rating_count})</span>
                </div>
              ) : (
                <div className="mt-2 text-xs text-zinc-500">{t("noReviews")}</div>
              )}
            </div>
          </div>

          {data.description && (
            <p className="mt-4 text-sm text-zinc-700 leading-relaxed">{data.description}</p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="card-surface p-3 text-center" data-testid="stat-completed">
            <Award className="h-4 w-4 mx-auto text-emerald-700" />
            <div className="text-lg font-bold mt-1">{data.completed_orders}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("completed")}</div>
          </div>
          <div className="card-surface p-3 text-center" data-testid="stat-accepted">
            <Send className="h-4 w-4 mx-auto text-zinc-700" />
            <div className="text-lg font-bold mt-1">{data.accepted_offers}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("accepted")}</div>
          </div>
          <div className="card-surface p-3 text-center" data-testid="stat-member-since">
            <CalendarDays className="h-4 w-4 mx-auto text-zinc-700" />
            <div className="text-sm font-bold mt-1 leading-tight">{fmtMonthYear(data.member_since, lang)}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("memberSince")}</div>
          </div>
        </div>

        {/* Categories */}
        {data.categories.length > 0 && (
          <div className="mt-5">
            <p className="label-caps mb-2">{t("categories")}</p>
            <div className="flex flex-wrap gap-2">
              {data.categories.map((c) => {
                const Icon = CATEGORY_ICONS[c] || CATEGORY_ICONS.other;
                return (
                  <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700">
                    <Icon className="h-3.5 w-3.5 text-emerald-600" /> {tCat(c)}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div className="mt-6">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold">{t("reviewsTitle")}</h3>
            <span className="text-xs text-zinc-500">{data.rating_count}</span>
          </div>
          {data.reviews.length === 0 ? (
            <div className="card-surface p-5 text-sm text-zinc-600 mt-3" data-testid="reviews-empty">{t("noReviews")}.</div>
          ) : (
            <div className="mt-3 space-y-3">
              {data.reviews.map((r) => (
                <div key={r.id} className="card-surface p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">{r.customer_name}</div>
                    <span className="text-xs text-zinc-400">{fmtMonthYear(r.created_at, lang)}</span>
                  </div>
                  <div className="mt-1"><StarRow value={r.rating} /></div>
                  {r.comment && <p className="text-sm text-zinc-700 mt-2">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link to="/" data-testid="back-home" className="text-sm text-emerald-700 font-semibold hover:underline">{t("backHome")}</Link>
        </div>
      </div>
    </div>
  );
}
